import { useRef, useLayoutEffect, type ReactNode } from 'react';
import {
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type Announcements,
  type SensorDescriptor,
  type SensorOptions,
} from '@dnd-kit/core';
import { useSortable, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ActionIcon, Box, Group } from '@mantine/core';
import { IconGripVertical, IconX } from '@tabler/icons-react';
import { useIsMobile } from '@/hooks/useIsMobile';

/** Shared sensor setup for both dashboard sortable grids. */
export function useDashboardDndSensors(): SensorDescriptor<SensorOptions>[] {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
}

/**
 * Screen-reader announcements that name the moved item. dnd-kit's defaults
 * say "Sortable item was moved into position 3 of 8" — meaningless when the
 * items are unlabeled tiles/charts to an AT user.
 */
export function buildDndAnnouncements(labelFor: (id: string) => string): Announcements {
  return {
    onDragStart: ({ active }) =>
      `Picked up ${labelFor(String(active.id))}. Use arrow keys to move, space to drop, escape to cancel.`,
    onDragOver: ({ active, over }) =>
      over
        ? `${labelFor(String(active.id))} is over the position of ${labelFor(String(over.id))}.`
        : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? `${labelFor(String(active.id))} dropped at the position of ${labelFor(String(over.id))}.`
        : `${labelFor(String(active.id))} dropped.`,
    onDragCancel: ({ active }) => `Moving ${labelFor(String(active.id))} was cancelled.`,
  };
}

export interface SortableItemProps {
  id: string;
  editMode: boolean;
  onRemove: () => void;
  /** Human label for accessible names ("Remove {label}", "Drag to reorder {label}") */
  removeLabel: string;
  children: ReactNode;
}

/**
 * Wraps a dashboard tile/chart in a dnd-kit sortable node. In edit mode it
 * shows a drag handle and a remove button — corner-hung over the card on
 * desktop, in an inline strip above the content on mobile — and applies the
 * HTML `inert` attribute to the wrapped child so its links/buttons are
 * unreachable by pointer, keyboard AND assistive tech while editing.
 */
export function SortableItem({
  id,
  editMode,
  onRemove,
  removeLabel,
  children,
}: SortableItemProps): JSX.Element {
  // Intentional breakpoint asymmetry: item chrome follows useIsMobile (991px,
  // tracks the 2-column grid flip) while the header Customize button follows
  // the visibleFrom="sm" RefreshButton pattern (768px).
  const isMobile = useIsMobile();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: !editMode,
  });

  // Block ALL interaction (pointer + keyboard + AT) with the child in edit
  // mode. React 18's JSX types have no `inert` prop, so apply it via the DOM.
  // useLayoutEffect (not useEffect) so the attribute lands before paint,
  // closing the one-frame window where children are focusable on entering
  // edit mode.
  const contentRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    if (editMode) el.setAttribute('inert', '');
    else el.removeAttribute('inert');
  }, [editMode]);

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        position: 'relative',
        zIndex: isDragging ? 10 : undefined,
        opacity: isDragging ? 0.4 : undefined, // ghost; the DragOverlay carries the lifted copy
        outline: editMode ? '1px dashed var(--mantine-color-gray-4)' : undefined,
        outlineOffset: 2,
        borderRadius: 'var(--mantine-radius-md)',
        // Flex column so the child card can stretch to the full grid-cell
        // height (equal-height tiles). In a content-sized context (charts
        // grid wrapper div) this is a no-op.
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {editMode && isMobile && (
        /*
         * Mobile: a slim inline strip ABOVE the child content, in normal
         * flow — never absolute. At 375px (2-col grid, compact `sm` tile
         * padding) the desktop corner-hang overlapped the tile's own label /
         * status icon AND let adjacent tiles' hit areas overlap across the
         * narrow gutter (wrong-target taps). Inline placement makes both
         * overlaps structurally impossible; 36px targets satisfy WCAG 2.5.8
         * (the collision that motivated 44px is gone).
         */
        <Group justify="space-between" gap="xs" wrap="nowrap" mb="xs">
          <ActionIcon
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            variant={isDragging ? 'filled' : 'subtle'}
            color="gray"
            size={36}
            aria-label={`Drag to reorder ${removeLabel}`}
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              touchAction: 'none',
            }}
          >
            <IconGripVertical size={16} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="coral"
            size={36}
            aria-label={`Remove ${removeLabel}`}
            onClick={onRemove}
          >
            <IconX size={16} />
          </ActionIcon>
        </Group>
      )}
      {editMode && !isMobile && (
        <>
          {/*
           * Desktop: the 44px hit targets are offset by -10px so the 16px
           * icon glyphs anchor into the dashed-outline gutter (outlineOffset
           * 2) and hang over the card corners instead of covering the tile's
           * top-left label / top-right status icon. Final visual tuning is
           * verified in the manual screenshot pass (plan Task 7).
           */}
          <ActionIcon
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            variant={isDragging ? 'filled' : 'subtle'}
            color="gray"
            size={44}
            aria-label={`Drag to reorder ${removeLabel}`}
            style={{
              position: 'absolute',
              top: -10,
              left: -10,
              zIndex: 11,
              cursor: isDragging ? 'grabbing' : 'grab',
              touchAction: 'none',
            }}
          >
            <IconGripVertical size={16} />
          </ActionIcon>
          <ActionIcon
            variant="subtle"
            color="coral"
            size={44}
            aria-label={`Remove ${removeLabel}`}
            onClick={onRemove}
            style={{ position: 'absolute', top: -10, right: -10, zIndex: 11 }}
          >
            <IconX size={16} />
          </ActionIcon>
        </>
      )}
      <div
        ref={contentRef}
        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
      >
        {children}
      </div>
    </Box>
  );
}
